import { Source, Version } from '.';

export enum ActionTypes {
    VIEW = 'VIEW', // View a widget or a widget element (eg a review)
    CLICK = 'CLICK', // Click on a widget component
    LOAD = 'LOAD', // A widget has loaded
    FILTER = 'FILTER', // A filter was added or removed
    TIMER = 'TIMER', // Leftover from v1, never fully baked anyway, do not use
    PAGEVIEW = 'PAGEVIEW', // A page with the plugin snippet was loaded, whether or not a widget was
    CONVERSION = 'CONVERSION', // A conversion event has occurred
    PING = 'PING', // A period ping can be sent to track engagement time with the widget
}

// Potential targets for view events
export enum ViewTargets {
    // Whole Widgets
    NEIGHBORHOOD_WIDGET = 'NEIGHBORHOOD_WIDGET', // The neighborhood widget is visible on page
    MAP_WIDGET = 'MAP_WIDGET', // The map widget is visible on page
    PAGE_WIDGET = 'PAGE_WIDGET', // The page widget is visible on page
    // Components within Widgets
    TARGET_LAYER_AREA = 'TARGET_LAYER_AREA', // The target layer area has become visible
    SERVICE_AREA_SUMMARY = 'SERVICE_AREA_SUMMARY', // User has viewed a summarized service area card
    SERVICE_AREA_DETAILS = 'SERVICE_AREA_DETAILS', // User has expanded the details for a service area
    REVIEW_SUMMARY = 'REVIEW_SUMMARY', // User has viewed a summarized review card
    REVIEW_DETAILS = 'REVIEW_DETAILS', // User has expaneded the review details
    JOB_SUMMARY = 'JOB_SUMMARY', // User has viewed a summarized job card
    JOB_DETAILS = 'JOB_DETAILS', // User has expanded the job details
    MEDIA_THUMBNAIL = 'MEDIA_THUMBNAIL', // User has viewed a media thumbnail
    MEDIA_FULL = 'MEDIA_FULL', // User has viewed a zoomed in or full-size media
}

// Potential targets for click events
export enum ClickTargets {
    ALL_AREAS = 'ALL_AREAS', // User has clicked to go back to viewing all service areas
    SERVICE_AREA = 'SERVICE_AREA', // User has clicked on a service area eg on the map
    REVIEW = 'REVIEW', // User has clicked on a review
    JOB = 'JOB', // User has clicked on a job
    MEDIA = 'MEDIA', // User has clicked on media
    SEE_ALL = 'SEE_ALL', // User has clicked the SEE ALL button
    NEXT = 'NEXT', // User has clicked the next button
    PREV = 'PREV', // User has clicked the previous button
    SEARCH_FOCUSED = 'SEARCH_FOCUSED', // User has clicked/focused on the search input
    SEARCH_FINISHED = 'SEARCH_FINISHED', // User has performed a search
    NEAR_ME = 'NEAR_ME', // User has clicked the near me button
    EXTERNAL_REVIEWS = 'EXTERNAL_REVIEWS', // User has clicked on a link to view reviews externally
    CLOSE = 'CLOSE', // User has clicked a close button
    CONTACT = 'CONTACT', // User has clicked the contact button - use when there is only one CTA up at a time
    // Events for specific types of contact button when there is more than one
    CONTACT_PHONE = 'CONTACT_PHONE',
    CONTACT_EMAIL = 'CONTACT_EMAIL',
    CONTACT_FORM = 'CONTACT_FORM',
    BUMP_OUT_OPENED = 'BUMP_OUT_OPENED', // User has clicked to open the bump out tab
    BUMP_OUT_CLOSED = 'BUMP_OUT_CLOSED', // User has clicked to close the bump out tab
}

export enum ConversionType {
    Email = 'Email',
    Phone = 'Phone',
    StartWebForm = 'StartWebForm',
    FinishWebForm = 'FinishWebForm',
    Subscribe = 'Subscribe',
    Review = 'Review',
    Chat = 'Chat',
    ContactUs = 'ContactUs',
    Share = 'Share',
}

export enum TargetLayer {
    SITE = 'SITE', // Used for events that don't relate to a specific widget i.e. pageview and conversion
    WIDGET = 'WIDGET', // The entire widget, used when the widget doesn't have unique views/layers
    FEATURED = 'FEATURED',
    SERVICE_AREA = 'SERVICE_AREA',
    JOB = 'JOB',
    ALL_REVIEWS = 'ALL_REVIEWS',
    MEDIA_GALLERY = 'MEDIA_GALLERY',
    ZIPCODE_GATE = 'ZIPCODE_GATE', // The widget is showing a gate to enter a zip code
    BUMP_OUT = 'BUMP_OUT', // The widget is showing a bump out tab
}

export enum TargetLayerArea {
    HEADER = 'HEADER',
    FILTERS = 'FILTERS',
    REVIEWS = 'REVIEWS',
    JOBS = 'JOBS',
    MAP = 'MAP',
    MEDIA = 'MEDIA',
}

export interface UserActionView extends UserAction {
    segment: UserActionSegment<ActionTypes.VIEW>;
    data: UserActionData & {
        url?: string | null;
        target?: ViewTargets | null;
    };
}

export interface UserActionClick extends UserAction {
    segment: UserActionSegment<ActionTypes.CLICK>;
    data: UserActionData & {
        url?: string | null;
        target?: ClickTargets | null;
    };
}

export interface UserActionLoad extends UserAction {
    segment: UserActionSegment<ActionTypes.LOAD>;
    data: UserActionData;
}

export interface UserActionFilter extends UserAction {
    segment: UserActionSegment<ActionTypes.FILTER>;
    data: UserActionData & {
        filter?: string | null;
        wasAdded?: boolean | null;
    };
}

export interface UserActionPageView extends UserAction {
    segment: UserActionSegment<ActionTypes.PAGEVIEW>;
    data: UserActionData & {
        pageUrl?: string | null;
        referrer?: string | null;
    };
}

export interface UserActionConversion extends UserAction {
    segment: UserActionSegment<ActionTypes.CONVERSION>;
    data: UserActionData & {
        conversionType: ConversionType;
        pageUrl?: string;
        ruleId?: string;
        target?: string;
    };
}

export interface UserActionPing extends UserAction {
    segment: UserActionSegment<ActionTypes.PING>;
    data: UserActionData & {
        widgetIsInView?: boolean;
        userHasViewed?: boolean;
    };
}

export interface UserAction {
    _id: string;
    version: Version;
    collection: 'UserAction';
    capturedAt: Date;
    segment: UserActionSegment;
    data: UserActionData;
}

interface UserActionSegment<T = ActionTypes> {
    account: string;
    session?: string | null;
    property?: string | null;
    type: T;
    source: Source;
}

interface UserActionData {
    targetLayer?: TargetLayer | string | null; // The layer the event was captured on
    targetLayerId?: string | null; // If the layer has a specific ID, such as a job ID
    targetLayerArea?: TargetLayerArea | string | null; // The area within the layer the event was captured on
    workSite?: string | null;
    content?: string | null;
    media?: string | null;
    serviceArea?: string | null;
    // Bump-out specific fields
    ctaVariant?: string | null;
    deviceType?: 'mobile' | 'tablet' | 'desktop' | null;
    panelSide?: 'left' | 'right' | null;
    tabPosition?: 'top' | 'middle' | 'bottom' | null;
    sessionDuration?: number | null;
    scrollDepth?: number | null;
    interactionType?: string | null;
    loadTimeMs?: number | null;
    submittedZip?: string | null;
}
